// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/city/CityPlaces.tsx
// 🔧 PURPOSE: "Da vedere" delle pagine città. In evidenza il luogo più
//   vicino all'ospite (dalla posizione e dalle coordinate delle attrazioni;
//   senza posizione, il primo della lista) con "Portami lì" e "Scopri";
//   sotto gli altri in due colonne, dal più vicino se la posizione c'è,
//   altrimenti nell'ordine di sempre. Le cuffie segnano le tappe
//   dell'audioguida. Ogni card apre la pagina dell'attrazione, con lo
//   stesso indice di prima (/cities/<slug>/attraction/<indice>).
// ============================================

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Headphones, Navigation } from 'lucide-react';
import { Attraction } from '../../pages/cities/AttractionPage';
import { getDistanceMeters } from '../../hooks/useGeolocation';
import { intlLocale } from '../../lib/locale';

/** Passo a piedi: 80 metri al minuto, per eccesso (come la spiaggia di Esplora). */
const WALK_METERS_PER_MINUTE = 80;
/** Oltre i 3 km i minuti a piedi non servono: si dicono i chilometri. */
const WALK_MAX_METERS = 3000;

interface CityPlacesProps {
  slug: string;
  attractions: Attraction[];
  position: { lat: number; lon: number } | null;
  /** Gli id delle tappe dell'audioguida (vuoto se la città non ne ha una). */
  tourStopIds: string[];
}

interface Place {
  attraction: Attraction;
  /** L'indice nella config: è quello della rotta della pagina di dettaglio. */
  index: number;
  meters: number | null;
  inTour: boolean;
}

/** "Castello Angioino" → "castello-angioino": per riconoscere una tappa anche dal nome. */
function slugify(value: string): string {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * La prima frase del testo: fino al primo punto seguito da uno spazio (così
 * "4.500 anni" non la spezza) o al primo punto cinese, che non ha spazi dopo.
 */
function firstSentence(text: string): string {
  const clean = String(text || '').trim();
  const match = /^[\s\S]*?(?:[.!?](?=\s|$)|[。！？])/.exec(clean);
  return match ? match[0] : clean;
}

/** La foto; se non c'è o non si carica resta lo sfondo del contenitore. */
export const PlacePhoto: React.FC<{ src?: string; className?: string }> = ({ src, className = '' }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return null;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={`absolute inset-0 w-full h-full object-cover ${className}`}
    />
  );
};

const CityPlaces: React.FC<CityPlacesProps> = ({ slug, attractions, position, tourStopIds }) => {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.language);

  const places: Place[] = attractions.map((attraction, index) => ({
    attraction,
    index,
    meters:
      position && typeof attraction.lat === 'number' && typeof attraction.lon === 'number'
        ? getDistanceMeters(position.lat, position.lon, attraction.lat, attraction.lon)
        : null,
    inTour: tourStopIds.indexOf(attraction.id) >= 0 || tourStopIds.indexOf(slugify(attraction.name)) >= 0,
  }));

  // Con la posizione: dal più vicino (chi non ha coordinate va in fondo).
  // Senza: l'ordine della config, e in evidenza il primo.
  const byDistance = places.some((p) => p.meters !== null);
  const ordered = byDistance
    ? places.slice().sort((a, b) => {
        if (a.meters === null) return b.meters === null ? a.index - b.index : 1;
        if (b.meters === null) return -1;
        return a.meters - b.meters;
      })
    : places;
  const featured = ordered[0] || null;
  const others = ordered.slice(1);

  /** "6 min a piedi" (lungo) e "6 min" (pillola), oltre i 3 km "12,4 km". */
  const distanceOf = (meters: number | null): { long: string; short: string } | null => {
    if (meters === null) return null;
    if (meters <= WALK_MAX_METERS) {
      const minutes = Math.max(1, Math.ceil(meters / WALK_METERS_PER_MINUTE));
      return {
        long: t('explore.hero.walk', { count: minutes }),
        short: t('cities.page.see.walkShort', { value: minutes }),
      };
    }
    const km = meters / 1000;
    const digits = km < 10 ? 1 : 0;
    const value = new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(km);
    const label = t('explore.live.map.kilometers', { value });
    return { long: label, short: label };
  };

  const pathOf = (place: Place) => `/cities/${slug}/attraction/${place.index}`;
  const button =
    'flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl px-3 text-[14px] font-semibold whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400 motion-safe:transition-transform motion-safe:active:scale-[0.97]';

  const featuredDistance = featured ? distanceOf(featured.meters) : null;

  return (
    <section id="da-vedere" aria-labelledby="da-vedere-title">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 id="da-vedere-title" className="font-display font-semibold text-[23px] leading-tight text-nva-ink dark:text-stone-100 outline-none">
          {t('cities.page.see.title')}
        </h2>
        {byDistance && (
          <span className="text-[13px] text-nva-muted dark:text-stone-400 whitespace-nowrap">{t('cities.page.see.nearestFirst')}</span>
        )}
      </div>

      {featured && (
        <article className="rounded-[20px] overflow-hidden bg-white dark:bg-stone-900 border border-nva-line dark:border-stone-800">
          <div className="relative h-[180px] bg-gradient-to-br from-nva-sea to-cyan-700">
            <PlacePhoto src={featured.attraction.imageUrl} />
            <span className="absolute left-3 top-3 rounded-full bg-white text-nva-ink text-[12px] font-bold leading-none px-2.5 py-1.5 shadow-sm">
              {t('cities.page.see.mustSee')}
            </span>
            {featuredDistance && (
              <span className="absolute right-3 top-3 rounded-full bg-emerald-800 text-white text-[12px] font-bold leading-none px-2.5 py-1.5 shadow-sm tabular-nums">
                {featuredDistance.long}
              </span>
            )}
          </div>
          <div className="px-3.5 pt-3 pb-3.5">
            <h3 className="font-display font-semibold text-[21px] leading-tight text-nva-ink dark:text-stone-100">
              {featured.attraction.name}
            </h3>
            <p className="mt-1 text-[14px] leading-[1.45] text-stone-700 dark:text-stone-300 line-clamp-3">
              {firstSentence(t(featured.attraction.descriptionKey))}
            </p>
            <div className={`mt-3 grid gap-2 ${featured.attraction.mapUrl ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {featured.attraction.mapUrl && (
                <a
                  href={featured.attraction.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('cities.page.see.takeMeAria', { name: featured.attraction.name })}
                  className={`${button} border border-nva-line dark:border-stone-700 bg-white dark:bg-stone-900 text-nva-ink dark:text-stone-100`}
                >
                  <Navigation className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span className="truncate">{t('cities.page.see.takeMe')}</span>
                </a>
              )}
              <Link
                to={pathOf(featured)}
                aria-label={t('cities.page.see.discoverAria', { name: featured.attraction.name })}
                className={`${button} bg-nva-sea text-white dark:bg-sky-400 dark:text-stone-950`}
              >
                <span className="truncate">{t('explore.trips.discover')}</span>
                <ArrowRight className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </article>
      )}

      {others.length > 0 && (
        <ul className="mt-3 grid grid-cols-2 gap-2.5">
          {others.map((place) => {
            const distance = distanceOf(place.meters);
            return (
              <li key={place.attraction.id} className="min-w-0">
                <Link
                  to={pathOf(place)}
                  className="block h-full rounded-2xl overflow-hidden bg-white dark:bg-stone-900 border border-nva-line dark:border-stone-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400 motion-safe:transition-transform motion-safe:active:scale-[0.98]"
                >
                  <span className="relative block h-[110px] bg-gradient-to-br from-stone-300 to-stone-400 dark:from-stone-700 dark:to-stone-800">
                    <PlacePhoto src={place.attraction.imageUrl} />
                    {distance && (
                      <span className="absolute left-2 bottom-2 rounded-full bg-white/95 dark:bg-stone-900/90 text-nva-ink dark:text-stone-100 text-[11.5px] font-bold leading-none px-2 py-1 tabular-nums">
                        <span aria-hidden="true">{distance.short}</span>
                        <span className="sr-only">{distance.long}</span>
                      </span>
                    )}
                    {place.inTour && (
                      <span
                        role="img"
                        aria-label={t('cities.page.see.inTour')}
                        className="absolute right-2 top-2 flex items-center justify-center h-[26px] w-[26px] rounded-full bg-[#0b3b5c] text-white shadow-sm"
                      >
                        <Headphones className="h-[13px] w-[13px]" strokeWidth={2.4} aria-hidden="true" />
                      </span>
                    )}
                  </span>
                  <span className="block px-2.5 pt-2 pb-2.5 text-[14.5px] font-semibold leading-tight text-nva-ink dark:text-stone-100 line-clamp-2">
                    {place.attraction.name}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default CityPlaces;
